import {
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState, useCallback } from "react";

export interface EditableFieldProps {
  label: string;
  value: string | number | boolean | null | undefined;
  id?: string;
  type?:
    | "text"
    | "checkbox"
    | "number"
    | "select"
    | "email"
    | "textarea"
    | "password";
  onChange: (e: React.ChangeEvent<HTMLElement>) => void;
  name?: string;
  required?: boolean;
  requiredAsterisk?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  options?: { key: string; value: string }[];
  placeholder?: string;
  rows?: number;
  title?: string;
  autocomplete?: "on" | "off";
  validationDelay?: number;
  customError?: string;
  apiError?: "conflict" | string;
  disabled?: boolean;
  step?: number| string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  id,
  type = "text",
  onChange,
  name,
  required = false,
  requiredAsterisk = true,
  minLength,
  maxLength,
  min,
  max,
  pattern,
  options = [],
  placeholder = "",
  rows = 3,
  title,
  autocomplete = "on",
  validationDelay = 500,
  customError,
  apiError,
  step,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [validationTimeout, setValidationTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (apiError === "conflict" && customError) {
      setError(customError);
      setTouched(true);
    }
  }, [apiError, customError]);

  const validateInput = useCallback(
    (value: any): string => {
      if (
        required &&
        (!value || (typeof value === "string" && !value.trim()))
      ) {
        return "Este campo es obligatorio";
      }

      if (type === "email" && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.toString())) {
          return "Por favor, introduce una dirección de correo electrónico válida";
        }
      }

      if (minLength && typeof value === "string" && value.length < minLength) {
        return `Debe tener al menos ${minLength} caracteres`;
      }

      if (maxLength && typeof value === "string" && value.length > maxLength) {
        return `No debe tener más de ${maxLength} caracteres`;
      }

      if (type === "number") {
        const numValue = Number(value);
        if (min !== undefined && numValue < min) {
          return `Debe ser al menos ${min}`;
        }
        if (max !== undefined && numValue > max) {
          return `No debe ser más de ${max}`;
        }
      }

      if (pattern && typeof value === "string") {
        const regex = new RegExp(pattern);
        if (!regex.test(value)) {
          return "Por favor, sigue el formato solicitado";
        }
      }

      return "";
    },
    [required, type, minLength, maxLength, min, max, pattern]
  );

  const debouncedValidation = useCallback(
    (value: any) => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }

      const timeout = setTimeout(() => {
        setError(validateInput(value));
      }, validationDelay);

      setValidationTimeout(timeout);
    },
    [validationDelay, validateInput, validationTimeout]
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const newValue =
      type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setInputValue(newValue);
    setTouched(true);
    setError("");

    if (type === "checkbox" || type === "select") {
      setError(validateInput(newValue));
    } else if (required || newValue) {
      debouncedValidation(newValue);
    }

    onChange(e);
  };

  const handleBlur = () => {
    if (!required && !inputValue) return;
    setTouched(true);
    if (validationTimeout) {
      clearTimeout(validationTimeout);
      setValidationTimeout(null);
    }
    setError(validateInput(inputValue));
  };

  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  const getInputClassName = (baseClass: string) => {
    return `${baseClass} ${
      error && touched
        ? "border-2 border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
    }`;
  };

  const commonProps = {
    name,
    id: id || name,
    required,
    minLength,
    maxLength,
    min,
    max,
    pattern,
    placeholder,
    title,
    disabled,
    "data-testid": id || name,
    autoComplete: autocomplete,
    onBlur: handleBlur,
    step: step,
  };

  const renderInput = () => {
    if (type === "checkbox") {
      return (
        <div className="flex flex-col space-y-1">
          <label
            className="text-base font-medium text-gray-700"
            htmlFor={id || name}
          >
            {label}
            {required && requiredAsterisk && (
              <span className="text-red-500 ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
          <input
            type="checkbox"
            checked={inputValue as boolean}
            onChange={handleChange}
            {...{
              ...commonProps,
              className: getInputClassName(
                "h-4 w-4 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500"
              ),
            }}
          />
          {error && touched && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>
      );
    }

    if (type === "select" && options) {
      const selectValue =
        inputValue === undefined || inputValue === null ? "" : inputValue;

      return (
        <div className="flex flex-col space-y-1">
          <div className="relative">
            <select
              value={selectValue as string}
              onChange={handleChange}
              {...commonProps}
              className={getInputClassName(
                "w-full rounded-md py-2 px-3 pr-10 appearance-none bg-white transition-colors duration-200"
              )}
            >
              <option value="">{placeholder}</option>
              {options.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.value}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {error && touched && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-10 pointer-events-none">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (type === "textarea") {
      return (
        <textarea
          value={inputValue?.toString() ?? ""}
          onChange={handleChange}
          rows={rows}
          {...commonProps}
          className={getInputClassName(
            "w-full rounded-md py-2 px-3 min-h-[100px] transition-colors duration-200"
          )}
        />
      );
    }

    if (type === "password") {
      return (
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={inputValue?.toString() ?? ""}
            onChange={handleChange}
            {...commonProps}
            className={getInputClassName(
              "w-full rounded-md py-2 pl-3 pr-10 transition-colors duration-200"
            )}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            {showPassword ? (
              <EyeSlashIcon className="h-6 w-6 text-gray-800" />
            ) : (
              <EyeIcon className="h-6 w-6 text-gray-800" />
            )}
          </button>
        </div>
      );
    }

    return (
      <input
        type={type}
        value={inputValue?.toString() ?? ""}
        onChange={handleChange}
        onWheel={(e) => (e.target as HTMLElement).blur()}
        {...commonProps}
        className={getInputClassName(
          "w-full rounded-md py-2 px-3 transition-colors duration-200"
        )}
      />
    );
  };

  if (type === "checkbox") {
    return (
      <div className="flex flex-col space-y-1">
        {renderInput()}
        {error && touched && (
          <p className="mt-1 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-1">
      <label
        className="text-base font-medium text-gray-700"
        htmlFor={id || name}
      >
        {label}
        {required && requiredAsterisk && (
          <span className="text-red-500 ml-1" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div className="relative">
        {renderInput()}
        {error && touched && type !== "password" && type !== "select" && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      {error && touched && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
